import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { providersAPI } from '../api/providers';
import { useAuth } from '../hooks/useAuth';
import Avatar from '../components/common/Avatar';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import Modal from '../components/common/Modal';
import BookingForm from '../components/booking/BookingForm';
import {
  MapPinIcon,
  PhoneIcon,
  StarIcon,
  ClockIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  PlusIcon,
  SparklesIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { formatCurrency } from '../utils/formatter';
import toast from 'react-hot-toast';

const ProviderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isClient } = useAuth();
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleRead = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    fetchProviderDetails();
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated || !isClient || services.length === 0 || showBookingModal) return;

    const params = new URLSearchParams(location.search);
    const serviceId = params.get('bookService');
    if (!serviceId) return;

    const serviceToBook = services.find((service) => String(service.id) === serviceId);
    if (!serviceToBook) return;

    setSelectedService(serviceToBook);
    setShowBookingModal(true);
    params.delete('bookService');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [isAuthenticated, isClient, services, showBookingModal, location.pathname, location.search, location.hash, navigate]);

  const fetchProviderDetails = async () => {
    setLoading(true);
    try {
      const [providerData, servicesData, reviewsData] = await Promise.all([
        providersAPI.getProvider(id),
        providersAPI.getProviderServices(id),
        providersAPI.getReviews(id),
      ]);
      setProvider(providerData);
      setServices(Array.isArray(servicesData) ? servicesData : servicesData?.services || []);
      setReviews(Array.isArray(reviewsData) ? reviewsData : reviewsData?.reviews || []);
    } catch (error) {
      console.error('Error fetching provider:', error);
      toast.error('Failed to load provider details');
    } finally {
      setLoading(false);
    }
  };

  const handleBookService = (service) => {
    if (!isAuthenticated) {
      toast.error('Please login to book a service');
      navigate('/login', { state: { from: `/providers/${id}?bookService=${service.id}` } });
      return;
    }
    if (!isClient) {
      toast.error('Only clients can book services');
      return;
    }
    setSelectedService(service);
    setShowBookingModal(true);
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const ratingDistribution = useMemo(() => {
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((r) => {
      if (dist[r.rating] !== undefined) dist[r.rating]++;
    });
    return dist;
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    if (reviewFilter === 'all') return reviews;
    if (reviewFilter === 'photos') return reviews.filter((r) => r.photos?.length > 0);
    const star = parseInt(reviewFilter);
    return reviews.filter((r) => r.rating === star);
  }, [reviews, reviewFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="text-center">
          <h2 className="text-2xl mb-4" style={{ color: 'var(--gray-12)' }}>Provider not found</h2>
          <Button onClick={() => navigate('/search')}>Back to Search</Button>
        </div>
      </div>
    );
  }

  const rating = provider.rating || 0;
  const totalReviews = provider.totalReviews || reviews.length;
  const DESC_LIMIT = 240;
  const REVIEW_LIMIT = 220;

  const h12 = (t) => {
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
  };

  const todaySlot = (provider.availability || []).find((a) => a.dayOfWeek === new Date().getDay());

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: '#fff', fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* ───────── PROFILE HEADER ───────── */}
        <div className="flex flex-col items-center gap-4 pb-8 border-b text-center sm:flex-row sm:items-start sm:gap-7 sm:text-left" style={{ borderColor: 'var(--gray-4)' }}>
          <div className="shrink-0">
            {provider.photoUrl ? (
              <img
                src={provider.photoUrl}
                alt={provider.businessName}
                className="w-20 h-20 sm:w-28 sm:h-28 rounded-full object-cover"
                style={{ border: '4px solid var(--orange-3)', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)' }}
              />
            ) : (
              <div
                className="w-20 h-20 sm:w-28 sm:h-28 rounded-full flex items-center justify-center text-3xl sm:text-4xl font-bold"
                style={{ backgroundColor: 'var(--orange-3)', color: 'var(--orange-9)', border: '4px solid var(--orange-3)', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)' }}
              >
                {provider.businessName?.charAt(0)?.toUpperCase()}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 w-full">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center justify-center gap-2 flex-wrap mb-2 sm:justify-start">
                  <h1 className="text-[30px] leading-none sm:text-3xl font-bold" style={{ color: 'var(--gray-12)', margin: 0 }}>
                    {provider.businessName}
                  </h1>
                  {provider.verifiedBadge && (
                    <CheckBadgeIcon
                      className="w-6 h-6 shrink-0"
                      style={{ color: 'var(--orange-9)' }}
                      title="Verified provider"
                    />
                  )}
                </div>

                <div className="flex items-center justify-center gap-3 flex-wrap mb-3 sm:justify-start">
                  <div className="flex items-center gap-1 justify-center">
                    {[1, 2, 3, 4, 5].map((s) =>
                      s <= Math.round(rating) ? (
                        <StarSolid key={s} style={{ width: '15px', height: '15px', color: '#facc15' }} />
                      ) : (
                        <StarIcon key={s} style={{ width: '15px', height: '15px', color: 'var(--gray-6)' }} />
                      )
                    )}
                  </div>
                  <span className="text-sm font-semibold" style={{ color: 'var(--gray-12)' }}>
                    {rating.toFixed(1)}
                  </span>
                  <span className="text-sm" style={{ color: 'var(--gray-10)' }}>
                    ({totalReviews} review{totalReviews !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>

            </div>

            {provider.description && (
              <p className="mx-auto max-w-2xl text-sm leading-relaxed mb-4 sm:mx-0" style={{ color: 'var(--gray-11)' }}>
                {provider.description}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-center gap-3 text-xs sm:justify-start" style={{ color: 'var(--gray-10)' }}>
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ backgroundColor: 'var(--gray-2)' }}>
                <MapPinIcon className="w-3.5 h-3.5" />
                {provider.location || 'Location unavailable'}
              </span>
              {provider.phone && (
                <a
                  href={`tel:${provider.phone}`}
                  className="inline-flex items-center gap-1.5 transition-colors rounded-full px-3 py-1.5"
                  style={{ color: 'var(--gray-10)', textDecoration: 'none', backgroundColor: 'var(--gray-2)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange-9)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-10)')}
                >
                  <PhoneIcon className="w-3.5 h-3.5" />
                  {provider.phone}
                </a>
              )}
              {provider.offersMobile && (
                <span className="inline-flex items-center gap-1.5 font-semibold rounded-full px-3 py-1.5" style={{ color: 'var(--orange-9)', backgroundColor: 'var(--orange-2)' }}>
                  <TruckIcon className="w-3.5 h-3.5" />
                  Mobile Service
                </span>
              )}
              <span
                className="inline-flex items-center gap-1.5 font-semibold rounded-full px-3 py-1.5"
                style={{ color: todaySlot ? '#16a34a' : 'var(--gray-10)', backgroundColor: todaySlot ? '#f0fdf4' : 'var(--gray-2)' }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: todaySlot ? '#16a34a' : 'var(--gray-8)' }}
                />
                {todaySlot ? 'Open today' : 'Closed today'}
              </span>
            </div>
          </div>
        </div>

        {/* ═════════ SERVICES ═════════ */}
        <section id="section-services" className="pt-10">
          <SectionTitle
            label="Services"
            count={services.length}
            hint={services.length === 1 ? '1 service offered' : `${services.length} services offered`}
          />
          {services.length === 0 ? (
            <EmptyState
              icon={<BuildingStorefrontIcon style={{ width: '28px', height: '28px', color: 'var(--gray-9)' }} />}
              title="No services yet"
              message="This provider hasn't listed any services."
            />
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {services.map((service) => {
                const key = `svc-${service.id}`;
                const isOpen = expanded.has(key);
                const desc = service.description || '';
                const isLong = desc.length > 140;
                const visible = !isLong || isOpen ? desc : `${desc.slice(0, 140).trim()}…`;
                return (
                  <li
                    key={service.id}
                    className="rounded-2xl border transition-all hover:shadow-md"
                    style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
                  >
                    <div className="flex items-stretch gap-4 p-3 sm:p-4">
                      {/* Thumbnail */}
                      <div
                        className="relative shrink-0 rounded-xl overflow-hidden"
                        style={{
                          width: '120px',
                          minHeight: '120px',
                          backgroundColor: 'var(--gray-3)',
                        }}
                      >
                        {service.imageUrl ? (
                          <img
                            src={service.imageUrl}
                            alt={service.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{ backgroundColor: 'var(--orange-2)' }}
                          >
                            <SparklesIcon className="w-9 h-9" style={{ color: 'var(--orange-9)' }} />
                          </div>
                        )}
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                          <h3
                            className="text-base font-bold"
                            style={{ color: 'var(--gray-12)', margin: 0 }}
                          >
                            {service.name}
                          </h3>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold"
                              style={{ backgroundColor: 'var(--gray-3)', color: 'var(--gray-12)' }}
                            >
                              <ClockIcon className="w-3 h-3" />
                              {service.duration} min
                            </span>
                            {service.isMobile && (
                              <span
                                className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold"
                                style={{ backgroundColor: 'var(--orange-9)', color: '#fff' }}
                              >
                                Mobile
                              </span>
                            )}
                            {service.isInShop && (
                              <span
                                className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-semibold border"
                                style={{ color: 'var(--gray-12)', borderColor: 'var(--gray-5)' }}
                              >
                                In-Shop
                              </span>
                            )}
                          </div>
                        </div>

                        {desc && (
                          <p
                            className="text-xs mb-1.5"
                            style={{ color: 'var(--gray-10)', lineHeight: 1.6, margin: 0 }}
                          >
                            {visible}
                          </p>
                        )}
                        {isLong && (
                          <button
                            type="button"
                            onClick={() => toggleRead(key)}
                            className="mb-1.5 text-xs font-semibold w-fit"
                            style={{ color: 'var(--orange-9)' }}
                          >
                            {isOpen ? 'Show less' : 'Read more'}
                          </button>
                        )}

                        {service.addons?.length > 0 && (
                          <div
                            className="inline-flex items-center gap-1 text-[11px] font-medium"
                            style={{ color: 'var(--gray-10)' }}
                          >
                            <PlusIcon className="w-3 h-3" />
                            {service.addons.length} add-on{service.addons.length !== 1 ? 's' : ''} available
                          </div>
                        )}

                        <div
                          className="flex items-center justify-between gap-3 mt-3 pt-3 border-t"
                          style={{ borderColor: 'var(--gray-4)' }}
                        >
                          <div className="leading-tight">
                            <div
                              className="text-[10px] uppercase tracking-wider font-semibold"
                              style={{ color: 'var(--gray-9)' }}
                            >
                              From
                            </div>
                            <div className="text-lg font-bold" style={{ color: 'var(--orange-9)' }}>
                              {formatCurrency(service.price)}
                            </div>
                          </div>
                          <button
                            onClick={() => handleBookService(service)}
                            className="h-9 px-4 rounded-full text-xs font-semibold text-white transition-colors inline-flex items-center gap-1.5 shrink-0"
                            style={{ backgroundColor: 'var(--orange-9)' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-10)')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-9)')}
                          >
                            Book Now
                            <ArrowRightIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <Divider />

        {/* ═════════ ABOUT ═════════ */}
        <section id="section-about">
          <SectionTitle
            label="About"
            hint={`Get to know ${provider.businessName}`}
          />

          {/* Compact stats row */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 rounded-2xl border overflow-hidden mb-6"
            style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
          >
            <StatCell
              icon={<StarSolid className="w-5 h-5" style={{ color: '#facc15' }} />}
              value={rating.toFixed(1)}
              label={`${totalReviews} review${totalReviews !== 1 ? 's' : ''}`}
            />
            <StatCell
              icon={<SparklesIcon className="w-5 h-5" style={{ color: 'var(--orange-9)' }} />}
              value={services.length}
              label={`service${services.length !== 1 ? 's' : ''}`}
            />
            <StatCell
              icon={<CalendarDaysIcon className="w-5 h-5" style={{ color: 'var(--orange-9)' }} />}
              value={(provider.availability || []).length}
              label="days/week"
            />
            <StatCell
              icon={<TruckIcon className="w-5 h-5" style={{ color: 'var(--orange-9)' }} />}
              value={provider.offersMobile ? 'Yes' : 'No'}
              label="mobile service"
            />
          </div>

          {/* Description with read-more */}
          {provider.description && (() => {
            const key = 'about-desc';
            const isOpen = expanded.has(key);
            const long = provider.description.length > DESC_LIMIT;
            const visible = !long || isOpen ? provider.description : `${provider.description.slice(0, DESC_LIMIT).trim()}…`;
            return (
              <div
                className="rounded-2xl border p-6 sm:p-8 mb-6"
                style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
              >
                <h3
                  className="text-[11px] uppercase tracking-[0.2em] font-bold mb-4"
                  style={{ color: 'var(--gray-9)' }}
                >
                  About {provider.businessName}
                </h3>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--gray-12)', margin: 0 }}
                >
                  {visible}
                </p>
                {long && (
                  <button
                    type="button"
                    onClick={() => toggleRead(key)}
                    className="mt-3 text-sm font-semibold inline-flex items-center gap-1"
                    style={{ color: 'var(--orange-9)' }}
                  >
                    {isOpen ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Contact + Working Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div
              className="rounded-2xl border p-6"
              style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
            >
              <h3
                className="text-[11px] uppercase tracking-[0.2em] font-bold mb-5"
                style={{ color: 'var(--gray-9)' }}
              >
                Contact & Info
              </h3>
              <div className="space-y-4">
                <InfoRow
                  icon={<MapPinIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />}
                  label="Location"
                  value={provider.shopAddress || provider.location}
                />
                {provider.phone && (
                  <InfoRow
                    icon={<PhoneIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />}
                    label="Phone"
                    value={
                      <a
                        href={`tel:${provider.phone}`}
                        style={{ color: 'var(--orange-9)', textDecoration: 'none' }}
                      >
                        {provider.phone}
                      </a>
                    }
                  />
                )}
                {provider.offersMobile && (
                  <InfoRow
                    icon={<TruckIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />}
                    label="Mobile Service"
                    value={`Available${provider.maxTravelKm ? ` (up to ${provider.maxTravelKm} km)` : ''}`}
                  />
                )}
                <InfoRow
                  icon={<ClockIcon className="w-4 h-4" style={{ color: todaySlot ? '#16a34a' : 'var(--gray-9)' }} />}
                  label="Today"
                  value={
                    todaySlot
                      ? `${h12(todaySlot.startTime)} – ${h12(todaySlot.endTime)}`
                      : 'Closed'
                  }
                />
              </div>
            </div>

            {(provider.availability?.length || 0) > 0 ? (
              <div
                className="rounded-2xl border p-6"
                style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />
                    <h3
                      className="text-[11px] uppercase tracking-[0.2em] font-bold"
                      style={{ color: 'var(--gray-9)', margin: 0 }}
                    >
                      Working Hours
                    </h3>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--gray-10)' }}>
                    {(provider.availability || []).length} days/week
                  </span>
                </div>
                <ul className="divide-y" style={{ borderColor: 'var(--gray-4)' }}>
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, i) => {
                    const slot = (provider.availability || []).find((a) => a.dayOfWeek === i);
                    const isToday = new Date().getDay() === i;
                    return (
                      <li
                        key={i}
                        className="flex justify-between items-center py-2.5 text-sm"
                        style={{ borderColor: 'var(--gray-4)', color: 'var(--gray-12)' }}
                      >
                        <span
                          className="flex items-center gap-2"
                          style={{ fontWeight: isToday ? 700 : 500 }}
                        >
                          {day}
                          {isToday && (
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: 'var(--orange-9)', color: '#fff' }}
                            >
                              TODAY
                            </span>
                          )}
                        </span>
                        <span
                          className="text-xs font-semibold"
                          style={{ color: slot ? 'var(--gray-12)' : 'var(--gray-9)' }}
                        >
                          {slot ? `${h12(slot.startTime)} – ${h12(slot.endTime)}` : 'Closed'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div
                className="rounded-2xl border p-6 flex items-center justify-center"
                style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
              >
                <div className="text-center">
                  <CalendarDaysIcon className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--gray-8)' }} />
                  <p className="text-sm" style={{ color: 'var(--gray-10)' }}>
                    Working hours not set
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Specializations + Languages */}
          {(provider.specializations?.length > 0 || provider.languages?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {provider.specializations?.length > 0 && (
                <div
                  className="rounded-2xl border p-6"
                  style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <SparklesIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />
                    <h3
                      className="text-[11px] uppercase tracking-[0.2em] font-bold"
                      style={{ color: 'var(--gray-9)', margin: 0 }}
                    >
                      Specializations
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {provider.specializations.map((spec) => (
                      <span
                        key={spec}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: 'var(--orange-2)',
                          color: 'var(--orange-10)',
                        }}
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {provider.languages?.length > 0 && (
                <div
                  className="rounded-2xl border p-6"
                  style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <GlobeAltIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />
                    <h3
                      className="text-[11px] uppercase tracking-[0.2em] font-bold"
                      style={{ color: 'var(--gray-9)', margin: 0 }}
                    >
                      Languages Spoken
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {provider.languages.map((lang) => (
                      <span
                        key={lang}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border"
                        style={{
                          backgroundColor: '#fff',
                          color: 'var(--gray-12)',
                          borderColor: 'var(--gray-5)',
                        }}
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hygiene & Safety */}
          {provider.hygieneNotes && (() => {
            const key = 'hygiene';
            const isOpen = expanded.has(key);
            const long = provider.hygieneNotes.length > DESC_LIMIT;
            const visible = !long || isOpen
              ? provider.hygieneNotes
              : `${provider.hygieneNotes.slice(0, DESC_LIMIT).trim()}…`;
            return (
              <div
                className="rounded-2xl border p-6"
                style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: '#dcfce7' }}
                  >
                    <ShieldCheckIcon className="w-5 h-5" style={{ color: '#16a34a' }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold mb-2" style={{ color: '#15803d', margin: 0 }}>
                      Hygiene & Safety Commitment
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--gray-12)', margin: 0 }}>
                      {visible}
                    </p>
                    {long && (
                      <button
                        type="button"
                        onClick={() => toggleRead(key)}
                        className="mt-2 text-xs font-semibold"
                        style={{ color: '#15803d' }}
                      >
                        {isOpen ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </section>

        <Divider />

        {/* ═════════ REVIEWS ═════════ */}
        <section id="section-reviews">
          <SectionTitle
            label="Reviews"
            count={totalReviews}
            hint={
              totalReviews === 0
                ? 'No reviews yet'
                : totalReviews === 1
                  ? `1 review · ${rating.toFixed(1)} avg`
                  : `${totalReviews} reviews · ${rating.toFixed(1)} avg`
            }
          />

          {reviews.length === 0 ? (
            <EmptyState
              icon={<StarIcon style={{ width: '28px', height: '28px', color: 'var(--gray-9)' }} />}
              title="No reviews yet"
              message="Be the first to leave a review after booking."
            />
          ) : (
            <div className="space-y-6">
              {/* Summary strip */}
              <div
                className="rounded-2xl border p-6 sm:p-8"
                style={{ backgroundColor: '#fff', borderColor: 'var(--gray-4)' }}
              >
                <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-8 md:gap-12 items-center">
                  <div
                    className="text-center md:text-left md:border-r md:pr-8"
                    style={{ borderColor: 'var(--gray-4)' }}
                  >
                    <div
                      className="text-5xl font-bold leading-none mb-2"
                      style={{ color: 'var(--gray-12)' }}
                    >
                      {rating.toFixed(1)}
                    </div>
                    <div className="flex items-center justify-center md:justify-start gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map((s) =>
                        s <= Math.round(rating) ? (
                          <StarSolid key={s} style={{ width: '16px', height: '16px', color: '#facc15' }} />
                        ) : (
                          <StarIcon key={s} style={{ width: '16px', height: '16px', color: 'var(--gray-6)' }} />
                        )
                      )}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--gray-10)' }}>
                      Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = ratingDistribution[star] || 0;
                      const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                      const isActive = reviewFilter === String(star);
                      return (
                        <button
                          key={star}
                          onClick={() =>
                            setReviewFilter(reviewFilter === String(star) ? 'all' : String(star))
                          }
                          className="w-full flex items-center gap-3 group"
                        >
                          <span
                            className="text-xs font-semibold w-10 text-left flex items-center gap-0.5"
                            style={{ color: isActive ? 'var(--orange-9)' : 'var(--gray-11)' }}
                          >
                            {star}
                            <StarSolid className="w-3 h-3" style={{ color: '#facc15' }} />
                          </span>
                          <div
                            className="flex-1 h-2 rounded-full overflow-hidden"
                            style={{ backgroundColor: 'var(--gray-3)' }}
                          >
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: isActive ? 'var(--orange-9)' : 'var(--orange-7)',
                              }}
                            />
                          </div>
                          <span
                            className="text-xs font-medium w-6 text-right"
                            style={{ color: 'var(--gray-10)' }}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Filter chips */}
                <div
                  className="mt-6 pt-5 border-t flex flex-wrap items-center gap-2"
                  style={{ borderColor: 'var(--gray-4)' }}
                >
                  <span
                    className="text-[11px] uppercase tracking-wider font-semibold mr-1"
                    style={{ color: 'var(--gray-9)' }}
                  >
                    Filter
                  </span>
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'photos', label: 'With photos' },
                    { id: '5', label: '5 stars' },
                    { id: '4', label: '4 stars' },
                    { id: '3', label: '3 stars' },
                  ].map((f) => {
                    const active = reviewFilter === f.id;
                    return (
                      <button
                        key={f.id}
                        onClick={() => setReviewFilter(f.id)}
                        className="h-8 px-3 rounded-full text-xs font-semibold transition-colors border"
                        style={{
                          backgroundColor: active ? 'var(--orange-9)' : '#fff',
                          color: active ? '#fff' : 'var(--gray-12)',
                          borderColor: active ? 'var(--orange-9)' : 'var(--gray-5)',
                        }}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Reviews list */}
              <div>
                <div
                  className="flex items-center justify-between pb-4 mb-2 border-b"
                  style={{ borderColor: 'var(--gray-4)' }}
                >
                  <h3 className="text-sm font-bold" style={{ color: 'var(--gray-12)' }}>
                    {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''}
                    {reviewFilter !== 'all' && (
                      <span className="font-normal" style={{ color: 'var(--gray-10)' }}>
                        {' '}· filtered
                      </span>
                    )}
                  </h3>
                  {reviewFilter !== 'all' && (
                    <button
                      onClick={() => setReviewFilter('all')}
                      className="text-xs font-semibold"
                      style={{ color: 'var(--orange-9)' }}
                    >
                      Clear filter
                    </button>
                  )}
                </div>

                {filteredReviews.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm" style={{ color: 'var(--gray-10)' }}>
                      No reviews match this filter.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y" style={{ borderColor: 'var(--gray-4)' }}>
                    {filteredReviews.map((review) => {
                      const key = `rev-${review.id}`;
                      const isOpen = expanded.has(key);
                      const comment = review.comment || '';
                      const long = comment.length > REVIEW_LIMIT;
                      const visible = !long || isOpen ? comment : `${comment.slice(0, REVIEW_LIMIT).trim()}…`;
                      return (
                        <li
                          key={review.id}
                          className="py-6"
                          style={{ borderColor: 'var(--gray-4)' }}
                        >
                          <div className="flex items-start gap-4 mb-3">
                            <Avatar
                              src={review.userAvatarUrl || review.user?.avatarUrl}
                              name={review.userName}
                              size={44}
                              bg="var(--orange-3)"
                              color="var(--orange-9)"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold" style={{ color: 'var(--gray-12)' }}>
                                    {review.userName}
                                  </span>
                                  <span className="text-xs" style={{ color: 'var(--gray-9)' }}>
                                    · {timeAgo(review.createdAt)}
                                  </span>
                                </div>
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map((s) =>
                                    s <= review.rating ? (
                                      <StarSolid key={s} style={{ width: '13px', height: '13px', color: '#facc15' }} />
                                    ) : (
                                      <StarIcon key={s} style={{ width: '13px', height: '13px', color: 'var(--gray-6)' }} />
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ paddingLeft: '60px' }}>
                            {comment && (
                              <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--gray-12)' }}>
                                {visible}
                              </p>
                            )}
                            {long && (
                              <button
                                type="button"
                                onClick={() => toggleRead(key)}
                                className="mb-3 text-xs font-semibold"
                                style={{ color: 'var(--orange-9)' }}
                              >
                                {isOpen ? 'Show less' : 'Read more'}
                              </button>
                            )}

                            {review.photos?.length > 0 && (
                              <div className="flex gap-2 mb-3 flex-wrap">
                                {review.photos.map((url, i) => (
                                  <img
                                    key={i}
                                    src={url}
                                    alt=""
                                    className="w-20 h-20 rounded-lg object-cover border"
                                    style={{ borderColor: 'var(--gray-4)' }}
                                  />
                                ))}
                              </div>
                            )}

                            {review.providerResponse && (
                              <div
                                className="rounded-xl p-4 border mt-3"
                                style={{
                                  backgroundColor: 'var(--orange-2)',
                                  borderColor: 'var(--orange-4)',
                                }}
                              >
                                <p
                                  className="text-[11px] font-bold uppercase tracking-wider mb-1"
                                  style={{ color: 'var(--orange-10)' }}
                                >
                                  Provider Response
                                </p>
                                <p
                                  className="text-sm leading-relaxed"
                                  style={{ color: 'var(--gray-12)', margin: 0 }}
                                >
                                  {review.providerResponse}
                                </p>
                              </div>
                            )}

                            <button
                              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold transition-colors"
                              style={{ color: 'var(--gray-10)' }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange-9)')}
                              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-10)')}
                            >
                              <HandThumbUpIcon className="w-3.5 h-3.5" />
                              Helpful
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>

      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedService && (
        <Modal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          title="Book Service"
          size="large"
        >
          <BookingForm
            provider={provider}
            service={selectedService}
            onSuccess={() => {
              setShowBookingModal(false);
              toast.success('Booking created successfully!');
              navigate('/bookings');
            }}
            onCancel={() => setShowBookingModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

const SectionTitle = ({ label, count, hint }) => (
  <div className="flex items-baseline gap-3 mb-5">
    <h2
      className="text-[11px] uppercase tracking-[0.22em] font-bold"
      style={{ color: 'var(--gray-9)', margin: 0 }}
    >
      {label}
    </h2>
    {count !== undefined && (
      <span
        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: 'var(--orange-3)', color: 'var(--orange-10)' }}
      >
        {count}
      </span>
    )}
    {hint && (
      <span className="text-xs" style={{ color: 'var(--gray-10)' }}>
        · {hint}
      </span>
    )}
  </div>
);

const Divider = () => (
  <div
    className="my-10"
    style={{ borderTop: '1px solid var(--gray-4)' }}
    aria-hidden
  />
);

const EmptyState = ({ icon, title, message }) => (
  <div
    className="text-center py-20 rounded-2xl border"
    style={{ borderColor: 'var(--gray-4)', backgroundColor: '#fff' }}
  >
    <div
      className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
      style={{ backgroundColor: 'var(--gray-3)' }}
    >
      {icon}
    </div>
    <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--gray-12)' }}>
      {title}
    </h3>
    <p className="text-sm" style={{ color: 'var(--gray-10)' }}>
      {message}
    </p>
  </div>
);

const StatCell = ({ icon, value, label }) => (
  <div
    className="flex flex-col items-center justify-center p-5 text-center border-r last:border-r-0 md:border-b-0"
    style={{ borderColor: 'var(--gray-4)' }}
  >
    <div className="mb-1">{icon}</div>
    <div className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--gray-12)' }}>
      {value}
    </div>
    <div className="text-[11px] uppercase tracking-wider font-semibold mt-0.5" style={{ color: 'var(--gray-9)' }}>
      {label}
    </div>
  </div>
);

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 shrink-0">{icon}</div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--gray-9)', margin: 0 }}>
        {label}
      </p>
      <p className="text-sm font-medium" style={{ color: 'var(--gray-12)', margin: 0 }}>
        {value}
      </p>
    </div>
  </div>
);

export default ProviderDetails;
